/**
 * Format case study content for better display
 * Converts markdown-style formatting to proper HTML with clear sections
 */
export const formatCaseStudyContent = (content) => {
  if (!content) return '';

  let formatted = content;

  // Replace **heading** with proper h3 elements (bold headings)
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<h3 class="text-xl font-bold text-gray-900 mt-6 mb-3 pt-4 border-t border-gray-200">$1</h3>');

  // Replace *text* with proper emphasis (italic)
  formatted = formatted.replace(/\*([^*]+)\*/g, '<em class="text-gray-700">$1</em>');

  // Convert line breaks to paragraphs for better spacing
  const lines = formatted.split('\n');
  const paragraphs = [];
  let currentParagraph = [];

  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine === '') {
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
    } else {
      currentParagraph.push(trimmedLine);
    }
  });

  if (currentParagraph.length > 0) {
    paragraphs.push(currentParagraph.join(' '));
  }

  // Wrap paragraphs in p tags with proper styling
  formatted = paragraphs.map((p, index) => {
    // Check if it's already a heading
    if (p.startsWith('<h3')) {
      return p;
    }
    // Regular paragraph
    return `<p class="text-gray-700 leading-relaxed mb-4">${p}</p>`;
  }).join('');

  return formatted;
};

/**
 * Parse case study content into structured sections and subsections
 * Each subsection ending with ":" becomes its own distinct box
 */
export const parseCaseStudySections = (content) => {
  if (!content) return { sections: [] };

  // First, clean up any remaining markdown formatting
  let cleanedContent = content
    .replace(/\*\*/g, '')  // Remove bold markdown
    .replace(/###/g, '')   // Remove heading markdown
    .replace(/##/g, '');   // Remove heading markdown

  const sections = [];
  const lines = cleanedContent.split('\n');
  let currentSection = null;
  let currentContent = [];

  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    // Check if line ends with : (subsection/box title)
    const subsectionMatch = trimmedLine.match(/^(.+):$/);
    
    if (subsectionMatch) {
      // Save previous section
      if (currentSection && currentContent.length > 0) {
        sections.push({
          title: currentSection,
          content: currentContent.join('\n').trim(),
          isTCM: isTCMSection(currentSection)
        });
      }
      
      // Start new section
      currentSection = subsectionMatch[1].trim();
      currentContent = [];
    } else if (trimmedLine && currentSection) {
      currentContent.push(trimmedLine);
    } else if (trimmedLine && !currentSection) {
      // Content before any heading - create a default section
      if (!currentSection) {
        currentSection = 'Case Study';
      }
      currentContent.push(trimmedLine);
    }
  });

  // Add last section
  if (currentSection && currentContent.length > 0) {
    sections.push({
      title: currentSection,
      content: currentContent.join('\n').trim(),
      isTCM: isTCMSection(currentSection)
    });
  }

  // If no sections found, treat all as one section
  if (sections.length === 0 && cleanedContent.trim()) {
    sections.push({
      title: 'Case Study',
      content: cleanedContent.trim(),
      isTCM: false
    });
  }

  return { sections };
};

/**
 * Check if a section title is TCM-specific
 */
const isTCMSection = (title) => {
  const tcmKeywords = [
    'ten questions',
    '10 questions',
    'eight principles',
    '8 principles',
    'treatment principle',
    'tcm diagnosis',
    'tongue',
    'pulse',
    'differential diagnosis',
    'pattern',
    'syndrome',
    'meridian',
    'points used',
    'acupuncture points'
  ];
  
  const lowerTitle = title.toLowerCase();
  return tcmKeywords.some(keyword => lowerTitle.includes(keyword));
};
